// Pure physics simulator for the craps dice throw. No Phaser imports.
// Bodies use circle colliders; "dice" use a square sprite visually but the
// simulation treats them as circles (cheap and good enough for two dice).

export type BodyKind = 'die' | 'chip';

export type Spring = {
  targetX: number;
  targetY: number;
  k: number;     // stiffness
  c: number;     // damping coefficient
};

export type Body = {
  id: string;
  kind: BodyKind;
  x: number; y: number;
  vx: number; vy: number;
  angle: number;
  angVel: number;
  /** Half the side length for dice (square OBB) or the radius for chips
   *  (circle). Both shapes are described by a single half-extent. */
  halfSize: number;
  mass: number;
  restitution: number;
  damping: number;       // multiplicative per-second factor; 1 = no damping
  angDamping: number;
  spring?: Spring;
  asleep: boolean;
  _settleFrames: number;
};

export type Bounds = { left: number; right: number; top: number; bottom: number };

export type ImpactEvent = {
  bodyId: string;
  speed: number;
  wall: 'left' | 'right' | 'top' | 'bottom';
};

export type SimulatorOptions = {
  bounds: Bounds;
  rng?: () => number;
  wallRestitution?: number;
  pairRestitution?: number;          // die-vs-die
  diceChipRestitution?: number;
  settleVelThreshold?: number;
  settleAngVelThreshold?: number;
  settleFrames?: number;
  impactSpeedThreshold?: number;
  /** Speed below which the slow-friction multiplier kicks in (px/s). */
  lowSpeedThreshold?: number;
  /** Extra multiplicative damping factor applied each tick when speed is
   *  below lowSpeedThreshold. Smaller = more friction. */
  lowSpeedDampingPerSec?: number;
  lowSpeedAngDampingPerSec?: number;
  onImpact?: (e: ImpactEvent) => void;
};

export interface Simulator {
  add(body: Body): void;
  remove(id: string): void;
  get(id: string): Body | undefined;
  setSpring(id: string, spring: Spring | undefined): void;
  step(dtSec: number): void;
  bodies(): readonly Body[];
}

export function createSimulator(opts: SimulatorOptions): Simulator {
  const list: Body[] = [];
  const byId = new Map<string, Body>();
  const wallR = opts.wallRestitution ?? 0.65;
  const impactThresh = opts.impactSpeedThreshold ?? 1500;
  const rng = opts.rng ?? Math.random;
  const pairR = opts.pairRestitution ?? 0.85;
  const dcR = opts.diceChipRestitution ?? 0.55;
  const settleVelTh = opts.settleVelThreshold ?? 30;
  const settleAngTh = opts.settleAngVelThreshold ?? 1;
  const settleFrames = opts.settleFrames ?? 6;
  const lowSpeedTh = opts.lowSpeedThreshold ?? 0;
  const lowSpeedDamp = opts.lowSpeedDampingPerSec ?? 1;
  const lowSpeedAngDamp = opts.lowSpeedAngDampingPerSec ?? 1;

  // World-axis half-extent of a body's silhouette. Dice are squares
  // (radius is the half-side) — at 45° their footprint reaches r*√2 along
  // both axes. Chips are circles — extent is just the radius.
  function extent(b: Body): number {
    if (b.kind === 'die') {
      return b.halfSize * (Math.abs(Math.cos(b.angle)) + Math.abs(Math.sin(b.angle)));
    }
    return b.halfSize;
  }

  function bounceWall(b: Body): void {
    const ext = extent(b);
    const speed = Math.hypot(b.vx, b.vy);
    let wall: ImpactEvent['wall'] | undefined;
    if (b.x < opts.bounds.left + ext && b.vx < 0) {
      b.x = opts.bounds.left + ext; b.vx = -b.vx * wallR; wall = 'left';
    } else if (b.x > opts.bounds.right - ext && b.vx > 0) {
      b.x = opts.bounds.right - ext; b.vx = -b.vx * wallR; wall = 'right';
    }
    if (b.y < opts.bounds.top + ext && b.vy < 0) {
      b.y = opts.bounds.top + ext; b.vy = -b.vy * wallR; wall = wall ?? 'top';
    } else if (b.y > opts.bounds.bottom - ext && b.vy > 0) {
      b.y = opts.bounds.bottom - ext; b.vy = -b.vy * wallR; wall = wall ?? 'bottom';
    }
    if (wall) {
      // Random angVel kick on bounce — adds visual interest.
      b.angVel += (rng() - 0.5) * speed * 0.01;
      if (speed >= impactThresh) {
        opts.onImpact?.({ bodyId: b.id, speed, wall });
      }
    }
  }

  // 2D moment of inertia about the body's center.
  // Square (side 2r): I = (2/3) * m * r²
  // Disk (radius r):  I = (1/2) * m * r²
  function inertia(b: Body): number {
    const r2 = b.halfSize * b.halfSize;
    return b.kind === 'die' ? (2 / 3) * b.mass * r2 : 0.5 * b.mass * r2;
  }

  // OBB-vs-OBB collision via the Separating Axis Theorem with linear AND
  // angular impulse. A square die that strikes another with a corner imparts
  // spin via the cross-product torque — the visible "lock and spin" feel.
  function resolveDicePair(a: Body, b: Body, restitution: number): void {
    const ca = Math.cos(a.angle), sa = Math.sin(a.angle);
    const cb = Math.cos(b.angle), sb = Math.sin(b.angle);
    // Local axes for each box (axis1 = local +x; axis2 = local +y).
    const axes: Array<{ x: number; y: number }> = [
      { x: ca, y: sa }, { x: -sa, y: ca },
      { x: cb, y: sb }, { x: -sb, y: cb },
    ];
    const ha = a.halfSize, hb = b.halfSize;
    const dx = b.x - a.x, dy = b.y - a.y;
    let minOverlap = Infinity;
    let nx = 1, ny = 0;
    for (const ax of axes) {
      // Project each OBB's half-extents onto this axis.
      const projA = ha * Math.abs(ax.x * ca + ax.y * sa)
                  + ha * Math.abs(ax.x * -sa + ax.y * ca);
      const projB = hb * Math.abs(ax.x * cb + ax.y * sb)
                  + hb * Math.abs(ax.x * -sb + ax.y * cb);
      const dist = Math.abs(dx * ax.x + dy * ax.y);
      const overlap = projA + projB - dist;
      if (overlap <= 0) return; // separating axis — no collision
      if (overlap < minOverlap) {
        minOverlap = overlap;
        const sign = (dx * ax.x + dy * ax.y) >= 0 ? 1 : -1;
        nx = ax.x * sign; ny = ax.y * sign;
      }
    }
    // Position correction along the contact normal.
    const totalMass = a.mass + b.mass;
    a.x -= nx * minOverlap * (b.mass / totalMass);
    a.y -= ny * minOverlap * (b.mass / totalMass);
    b.x += nx * minOverlap * (a.mass / totalMass);
    b.y += ny * minOverlap * (a.mass / totalMass);
    // Contact point ≈ midpoint of the centers. Cheap, but enough to seed
    // the angular impulse so corner hits actually spin the dice.
    const px = (a.x + b.x) / 2, py = (a.y + b.y) / 2;
    const rax = px - a.x, ray = py - a.y;
    const rbx = px - b.x, rby = py - b.y;
    // Velocities at the contact point (linear + ω×r).
    const vAx = a.vx - a.angVel * ray;
    const vAy = a.vy + a.angVel * rax;
    const vBx = b.vx - b.angVel * rby;
    const vBy = b.vy + b.angVel * rbx;
    const relVel = (vBx - vAx) * nx + (vBy - vAy) * ny;
    if (relVel > 0) return; // already separating
    const Ia = inertia(a), Ib = inertia(b);
    // 2D cross(r, n) = rx * ny - ry * nx
    const rACrossN = rax * ny - ray * nx;
    const rBCrossN = rbx * ny - rby * nx;
    const kN = 1 / a.mass + 1 / b.mass
             + (rACrossN * rACrossN) / Ia
             + (rBCrossN * rBCrossN) / Ib;
    const j = -(1 + restitution) * relVel / kN;
    const ix = j * nx, iy = j * ny;
    a.vx -= ix / a.mass; a.vy -= iy / a.mass;
    b.vx += ix / b.mass; b.vy += iy / b.mass;
    a.angVel -= rACrossN * j / Ia;
    b.angVel += rBCrossN * j / Ib;
  }

  // OBB-vs-circle collision (die vs chip). Find the closest point on the
  // OBB to the circle center, then resolve as circle-vs-circle there.
  function resolveDieChip(die: Body, chip: Body, restitution: number): void {
    const c = Math.cos(die.angle), s = Math.sin(die.angle);
    // Chip center in die's local frame.
    const dx = chip.x - die.x, dy = chip.y - die.y;
    const lx = dx * c + dy * s;
    const ly = -dx * s + dy * c;
    const clx = Math.max(-die.halfSize, Math.min(die.halfSize, lx));
    const cly = Math.max(-die.halfSize, Math.min(die.halfSize, ly));
    // Vector from closest point (local) to chip center (local).
    const ox = lx - clx, oy = ly - cly;
    const distSq = ox * ox + oy * oy;
    if (distSq >= chip.halfSize * chip.halfSize) return;
    const dist = Math.sqrt(distSq) || 0.0001;
    // Normal (in world coords) pointing from die surface to chip center.
    const lnx = ox / dist, lny = oy / dist;
    const nx = lnx * c - lny * s;
    const ny = lnx * s + lny * c;
    const overlap = chip.halfSize - dist;
    const totalMass = die.mass + chip.mass;
    die.x -= nx * overlap * (chip.mass / totalMass);
    die.y -= ny * overlap * (chip.mass / totalMass);
    chip.x += nx * overlap * (die.mass / totalMass);
    chip.y += ny * overlap * (die.mass / totalMass);
    // Relative velocity along the contact normal.
    const rvx = chip.vx - die.vx, rvy = chip.vy - die.vy;
    const relVel = rvx * nx + rvy * ny;
    if (relVel > 0) return;
    const j = -(1 + restitution) * relVel / (1 / die.mass + 1 / chip.mass);
    const ix = j * nx, iy = j * ny;
    die.vx -= ix / die.mass; die.vy -= iy / die.mass;
    chip.vx += ix / chip.mass; chip.vy += iy / chip.mass;
  }

  function step(dt: number): void {
    for (const b of list) {
      // 1. Spring force.
      if (b.spring) {
        const fx = b.spring.k * (b.spring.targetX - b.x) - b.spring.c * b.vx;
        const fy = b.spring.k * (b.spring.targetY - b.y) - b.spring.c * b.vy;
        b.vx += (fx / b.mass) * dt;
        b.vy += (fy / b.mass) * dt;
      }
      // 2. Integrate.
      b.x += b.vx * dt;
      b.y += b.vy * dt;
      b.angle += b.angVel * dt;
      // 3. Damping.
      if (b.damping !== 1) {
        const f = Math.pow(b.damping, dt);
        b.vx *= f; b.vy *= f;
      }
      if (b.angDamping !== 1) {
        const f = Math.pow(b.angDamping, dt);
        b.angVel *= f;
      }
      // 3b. Low-speed extra friction. Static-vs-kinetic analog: once a
      // body has slowed below the threshold, friction takes over and
      // brings it to a stop quickly instead of trickling.
      if (lowSpeedTh > 0 && !b.spring) {
        const speed = Math.hypot(b.vx, b.vy);
        if (speed < lowSpeedTh) {
          if (lowSpeedDamp !== 1) {
            const f = Math.pow(lowSpeedDamp, dt);
            b.vx *= f; b.vy *= f;
          }
          if (lowSpeedAngDamp !== 1) {
            const f = Math.pow(lowSpeedAngDamp, dt);
            b.angVel *= f;
          }
        }
      }
      // 4. Sleep tracking — sprung bodies never sleep.
      if (b.spring) {
        b.asleep = false;
        b._settleFrames = 0;
      } else {
        const speed = Math.hypot(b.vx, b.vy);
        const angSpeed = Math.abs(b.angVel);
        if (speed < settleVelTh && angSpeed < settleAngTh) {
          b._settleFrames++;
          if (b._settleFrames >= settleFrames) b.asleep = true;
        } else {
          b._settleFrames = 0;
          b.asleep = false;
        }
      }
      // 5. Walls — always. Sprung dice get clamped at the rail when the
      // finger drags past the edge; the spring fights against the wall and
      // the player feels a satisfying tug.
      bounceWall(b);
    }
    // Dice-vs-dice always resolves (sprung or not) so two dice held against
    // a single finger jostle each other instead of overlapping.
    const dice = list.filter((b) => b.kind === 'die');
    if (dice.length >= 2) resolveDicePair(dice[0]!, dice[1]!, pairR);
    // Dice-vs-chip only when the die is free — sprung dice ride above chips
    // during the grab so the player can wander over the felt without
    // disturbing the bet stacks.
    const chips = list.filter((b) => b.kind === 'chip' && !b.spring);
    const flyingDice = dice.filter((b) => !b.spring);
    for (const die of flyingDice) {
      for (const chip of chips) resolveDieChip(die, chip, dcR);
    }
  }

  return {
    add(b) { list.push(b); byId.set(b.id, b); },
    remove(id) {
      const b = byId.get(id);
      if (!b) return;
      const i = list.indexOf(b);
      if (i >= 0) list.splice(i, 1);
      byId.delete(id);
    },
    get(id) { return byId.get(id); },
    setSpring(id, spring) {
      const b = byId.get(id);
      if (!b) return;
      if (spring) b.spring = spring; else delete b.spring;
    },
    step,
    bodies() { return list; },
  };
}
